import { Capacitor } from '@capacitor/core'
import { Logger } from '@egw/logger'
import { notifications, NOTIFICATIONS } from '@egw/notifications'
import { type Config } from '../../config/config'
import { AppType } from '../../models'

const log = new Logger('Platform Service')

declare global {
  interface Window {
    matchMedia: (query: string) => MediaQueryList
  }
}

const defineAppType = (): string | null => {
  if (typeof window !== 'undefined') {
    let type = 'unknown'
    if (Capacitor.getPlatform() === 'web') {
      type = window.matchMedia('(display-mode: standalone)').matches ? AppType.Pwa : AppType.Browser
    } else if (Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios') {
      type = AppType.Capacitor
    }
    return type
  }
  return null
}

let instance: PlatformService | null = null

class PlatformService {
  private isInitialized = false

  private appName: string | undefined
  private appPlatform: string | undefined
  private appType: string | null | undefined
  private appVersion: string | undefined
  private isWebPlatform: boolean | undefined

  constructor() {
    if (!instance) {
      instance = this
    }
  }

  init(config: Config): void {
    if (!this.isInitialized) {
      if (typeof window !== 'undefined') {
        this.appName = config.appName
        this.appPlatform = Capacitor.getPlatform()
        this.appType = defineAppType()
        this.appVersion = config.appVersion
        this.isWebPlatform = Capacitor.getPlatform() === 'web'

        notifications.dispatch(NOTIFICATIONS.PLATFORM_SERVICE.INIT, {
          appName: this.appName,
          appPlatform: this.appPlatform,
          appType: this.appType,
          appVersion: this.appVersion,
          isWebPlatform: this.isWebPlatform,
        })
      }
      this.isInitialized = true
      log.info('Platform service initialized')
    } else {
      log.warn('Platform service is already initialized. Skipping initialization...')
    }
  }

  getAppName(): string {
    if (!this.isInitialized || !this.appName) {
      log.error('Service is not initialized')
      throw new Error('Service is not initialized')
    }
    return this.appName
  }

  getAppPlatform(): string {
    return this.appPlatform ?? Capacitor.getPlatform()
  }

  getAppType(): string | null {
    return this.appType ?? defineAppType()
  }

  getAppVersion(): string {
    if (!this.isInitialized || !this.appVersion) {
      log.error('Service is not initialized')
      throw new Error('Service is not initialized')
    }
    return this.appVersion
  }

  getIsWebPlatform(): boolean {
    return this.isWebPlatform ?? Capacitor.getPlatform() === 'web'
  }
}

export const platformServiceInstance = new PlatformService()
